import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { addStateFetch } from '@webapp/account/src/resources/store-utils';

import { arrDeleteById, updateItemInArray } from '@webapp/common/lib/utils';

import { patchObject } from '../lib';

import {
    addRecipient,
    addRecipientsFromCSV,
    addSms,
    addSmsGroup,
    changeSms,
    changeSmsGroupName,
    changeSmsParam,
    deleteSmsGroup,
    getSmsGroups,
    getSmsList,
    loadSmsing,
    removeRecipient,
    removeSmsGroupFromList,
    smsStatDownloadCSV,
} from './async';
import type { Sms, SmsGroup } from './smsing';
import { initialSms, initialSmsGroup } from './smsing';
import { initialState } from './state';

export const smsingSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        smsDetailsSetPage(s, { payload: { page } }: PayloadAction<{ page: number }>) {
            s.smsDetailsPage = page;
        },
        patchSmsDetailsFilters({ filters }, { payload: { change } }: PayloadAction<{ change: AnyObject }>) {
            patchObject(filters, change);
        },
        setSendDatetime(
            { item },
            { payload: { sendDatetime, sendNow } }: PayloadAction<{ sendDatetime: dayjs.Dayjs; sendNow: boolean }>,
        ) {
            patchObject(item, { sendNow, sendDatetime });
        },
        setExpireDatetime(
            { item },
            { payload: { expire, expireDatetime } }: PayloadAction<{ expire: boolean; expireDatetime: dayjs.Dayjs }>,
        ) {
            patchObject(item, { expire, expireDatetime });
        },
    },
    extraReducers: (builder) =>
        addStateFetch(
            builder
                .addCase(getSmsList.fulfilled, (s, { payload: { items } }) => {
                    s.items = items;
                    s.smsListLoaded = true;
                })
                .addCase(changeSmsParam.fulfilled, ({ item }, { payload: { params, duplicate } }) => {
                    item.params = params;
                    item.duplicate = duplicate;
                })
                .addCase(addSms.fulfilled, (s, { payload: { sms } }) => {
                    s.items.unshift(Object.assign(cloneDeep(initialSms), sms));
                })
                .addCase(changeSmsGroupName.fulfilled, ({ item, smsGroups }, { payload: { groupId, name } }) => {
                    updateItemInArray(smsGroups, groupId, (g) => {
                        g.name = name;
                    });
                    updateItemInArray(item.smsGroups, groupId, (g) => {
                        g.name = name;
                    });
                })
                .addCase(smsStatDownloadCSV.pending, (s) => {
                    s.csvDownloadFetch = true;
                })
                .addCase(smsStatDownloadCSV.rejected, (s) => {
                    s.csvDownloadFetch = false;
                })
                .addCase(smsStatDownloadCSV.fulfilled, (s, { payload: { file } }) => {
                    s.csvDownloadFetch = false;
                    window.open(file, 'blank');
                })
                .addCase(deleteSmsGroup.fulfilled, ({ item, smsGroups }, { payload: { groups } }) => {
                    arrDeleteById(smsGroups, groups);
                    arrDeleteById(item.smsGroups, groups);
                })
                .addCase(addSmsGroup.fulfilled, ({ item }, { payload: { phones, hidden, name, newGroupId } }) => {
                    const group: SmsGroup = {
                        ...cloneDeep(initialSmsGroup),
                        id: newGroupId,
                        hidden,
                        name,
                        phones: phones.map((phone) => ({ phone })),
                    };
                    item.smsGroups.push(group);
                })
                .addCase(addRecipient.fulfilled, (s, { payload: { recipient, id } }) => {
                    s.item.recipients.unshift({
                        recipient,
                        id,
                    });
                })
                .addCase(removeRecipient.fulfilled, ({ item }, { payload: { ids } }) => {
                    ids = new Set(ids); // TODO wtf
                    item.recipients = item.recipients.filter(({ id }) => !ids.has(id));
                })
                .addCase(getSmsGroups.fulfilled, (s, { payload: { smsGroups } }) => {
                    s.smsGroups = smsGroups;
                })
                .addCase(addRecipientsFromCSV.pending, (s) => {
                    s.fileUpload = true;
                })
                .addCase(addRecipientsFromCSV.rejected, (s) => {
                    s.fileUpload = false;
                })
                .addCase(addRecipientsFromCSV.fulfilled, (s, { payload: { group } }) => {
                    s.item.smsGroups.push(group);
                    s.fileUpload = false;
                })
                .addCase(loadSmsing.pending, ({ item }) => {
                    item.fetch = true;
                })
                .addCase(loadSmsing.fulfilled, (s, { payload: { item } }) => {
                    s.item = item;
                    s.item.fetch = false;
                })
                .addCase(removeSmsGroupFromList.fulfilled, ({ item: { smsGroups } }, { payload: { groups } }) => {
                    arrDeleteById(smsGroups, groups);
                })
                .addCase(changeSms.fulfilled, ({ item, items }, { payload: { change, id } }) => {
                    Object.assign(item, change);
                    updateItemInArray(items, id, (m: Sms) => {
                        Object.assign(m, change);
                    });
                }),
        ),
});
